import React, { useEffect, useState } from 'react';
import { SharedModal } from '../SharedModal';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import {
  listMetaPages,
  saveMetaSelection,
  type MetaConnectionStatus,
  type MetaSelectablePage,
} from '../../services/MetaConnectionService';
import './MetaAccountPicker.css';

export interface MetaAccountPickerProps {
  open: boolean;
  sectorId: number;
  onClose: () => void;
  /** Chamado depois de salvar; `errors` traz as Páginas que a Meta recusou. */
  onSaved: (status: MetaConnectionStatus, errors: Array<{ pageId: string; message: string }>) => void;
}

const serverMessage = (e: unknown, fallback: string) => {
  const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return msg?.trim() || apiErrorMessage(e, fallback);
};

const MetaAccountPicker: React.FC<MetaAccountPickerProps> = ({ open, sectorId, onClose, onSaved }) => {
  const [pages, setPages] = useState<MetaSelectablePage[] | null>(null);
  const [messenger, setMessenger] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setPages(null);
    setError(null);
    setLoading(true);
    listMetaPages(sectorId)
      .then((list) => {
        if (!alive) return;
        setPages(list);
        // Já conectadas vêm marcadas: salvar de novo atualiza o acesso.
        setMessenger(new Set(list.filter((p) => p.messengerConnected).map((p) => p.pageId)));
      })
      .catch((e) => alive && setError(serverMessage(e, 'Não foi possível listar as Páginas agora.')))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, sectorId]);

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  };

  const nothingSelected = messenger.size === 0;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await saveMetaSelection(sectorId, Array.from(messenger));
      onSaved(result.status, result.errors || []);
    } catch (e) {
      setError(serverMessage(e, 'Não foi possível salvar a escolha.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SharedModal open={open} onClose={onClose} title="Escolha o que conectar" size="md" className="meta-picker-modal">
      <div className="meta-picker">
        <p className="meta-picker__hint">
          Marque as Páginas do Facebook (Messenger) que vão atender por este setor. Só aparecem as Páginas em que você pode
          gerenciar mensagens. O Instagram não precisa de Página: conecte pelo botão "Conectar Instagram".
        </p>

        {loading && <p className="meta-picker__muted">Buscando suas Páginas na Meta…</p>}
        {error && <div className="meta-picker__error" role="alert">{error}</div>}
        {pages && pages.length === 0 && !error && (
          <p className="meta-picker__muted">
            Não encontramos Páginas com permissão de mensagens nesta conta da Meta. Confira se você administra a Página e
            tente conectar de novo.
          </p>
        )}

        {pages && pages.length > 0 && (
          <ul className="meta-picker__list">
            {pages.map((p) => (
              <li key={p.pageId} className="meta-picker__page">
                <div className="meta-picker__page-name">{p.pageName}</div>
                <label className="meta-picker__option">
                  <input
                    type="checkbox"
                    checked={messenger.has(p.pageId)}
                    onChange={() => toggle(messenger, setMessenger, p.pageId)}
                  />
                  <span>Messenger · {p.pageName}</span>
                </label>
              </li>
            ))}
          </ul>
        )}

        <div className="meta-picker__actions">
          <button type="button" className="meta-picker__btn" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button
            type="button"
            className="meta-picker__btn meta-picker__btn--primary"
            onClick={() => void save()}
            disabled={saving || loading || nothingSelected || !pages || pages.length === 0}
          >
            {saving ? 'Conectando…' : 'Conectar selecionados'}
          </button>
        </div>
      </div>
    </SharedModal>
  );
};

export default MetaAccountPicker;
